import {
  DataGridProProps,
  GridActionsCellItem,
  GridApiRef,
  GridColDef,
  GridColumnResizeParams,
  GridColumns,
  GridGroupingColDefOverride,
  GridRowId,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import React, {
  Dispatch,
  PropsWithChildren,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Controls from '../../controls/Controls';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { EditorModus } from '../EditDialog/EditDialog';
import LogDialog from '../LogDialog/LogDialog';
import AdminDataGridContextMenu, { CtxMenuItem } from './AdminDataGridCtxMenu';
import AdminDataGridEditPanel, { DetailViewProps, } from './AdminDataGridEditPanel';
import {
  useDataGridPagination,
  useDataGridRoutes,
  useDataGridSettings,
  useRegisterAccessRightsEvaluation,
} from './AdminDataGridHooks';
import { useStyles } from './AdminDataGridStyles';

import { useAppDispatch } from '../../../store';

import { SerializedError } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { ArgFilter, FetchArg, } from '../../../../api/rtk-queries/queryApiCreator';
import { ColumnSettings } from '../../../../models';
import { KeysArray } from '../../../../models/Constants/RequiredFields';
import { RegisterCodeName } from '../../../../models/Constants/ValgRegister';
import { EksportSettings } from '../../../../models/eksportSettings';
import { TEntity, tEntityId } from '../../../../models/entity';
import { BaseAttributes } from '../../../props';
import { Filter, FilterGroup, TypeFilter } from '../../../utils/filter';
import { ErrorObject } from '../../../utils/validateUtils';
import { TreeBaseProps } from '../TreeAdminDataGrid/TreeViewAdminDataGridMui';
import {
  CustomErrorOverlay,
  CustomToolbar,
  ExportComponentProps,
  GridNoRowsCustomOverlay,
} from './AdminDataGridCustomComponents';
import { useSaveDataGridEditPanel } from './AdminDataGridEditPanelHooks';
import { ImportProps } from './ImportComponent';
import { Pagination } from '@mui/material';


export type SaveProp<T extends TEntity> = {
  /**
   * The value might be a string when mode is EditorModus.Soek
   */
  save: (val: T, mode: EditorModus) => Promise<void> | void;
};
export type EntityProp<T extends TEntity> = {
  updateEntity(entity: T): any;
  createEntity(entity: T, mode?: EditorModus): any;
  searchEntity?(entity: T | string): any;
};

type BaseProps<T extends TEntity> = BaseAttributes & {
  getHeaderText?(id?: T['id'], mode?: EditorModus): string;
  registerTitle: string;
  itemTitle: string;

  canEdit?: boolean;
  canCreate?: boolean;
  canCopy?: boolean;
  canDelete?: boolean;

  canCopyEntity?: (id: T) => boolean;
  canDeleteEntity?: (id: T) => boolean;
  canEditEntity?: (id: T) => boolean;
  extendSimpleAccess?: boolean;

  getLogUrl(val: T): string;
  rows: T[] | GridRowsProp;
  columns: GridColDef[];
  registerId: RegisterCodeName;
  getDisplayName: (id: T['id'], mode?: EditorModus) => string | undefined;
  notSetInitialData?: boolean;
  requiredFields: KeysArray<T>;
  getErrors?: (fieldValues: Partial<T>, mode: EditorModus) => ErrorObject;
  loading: boolean;
  mutation?: boolean;
  toolbarItems?: ReactElement;
  configureCtxMenuItems?: (items: CtxMenuItem<T>[]) => CtxMenuItem<T>[];
  error?: string | { message: string };
  mutationErrors?: string;
  paginationMode?: 'server' | 'client';
  rowCount?: number;
  filterType?: TypeFilter;
  defaultFetchSetting?: FetchArg<T>;
  currentFilter?: Filter | undefined;
  loadEksport(eksportSettings: EksportSettings<T>): Promise<File | null>;
  filters?: FilterGroup[];
  onChangeSelectedFilterGroups?: (filterGroup: FilterGroup[]) => void;
  getId?: (val: T) => string | number;
  groupingColDef?: GridGroupingColDefOverride;
  getApiGridRef?: (apiRef: GridApiRef) => void;
  setFetchArg: Dispatch<SetStateAction<FetchArg<T>>>;
  prefetch?: (arg: FetchArg<T> | ArgFilter<T>) => void;
  refetch(args: FetchArg<T>): void;
  refresh?: () => void;
  remove(val: T): Promise<void> | void;
  resetMutation?: () => void;
  getConfirmRemoveMessage?(val: T): Promise<string | ReactElement>;
  confirmDeleteTitle?: string;
  deleteReport(id: tEntityId): Promise<{ status: QueryStatus, error?: SerializedError }>;
  renderDetailView(props: DetailViewProps<T>): JSX.Element;
  ImportComponent?: (props: ImportProps) => JSX.Element;
  exportComponentProps?: ExportComponentProps;
  searchMode: boolean;
  defaultFilter?: Filter;
  hideContextMenu?: boolean;
  onCreate?: () => void;
  rowClick?(p: GridRowParams<GridRowModel>): void;
};

export type TMoveEntityEvt = 'close' | 'clickEntity' | 'none';

export type AdminDataGridProps<T extends TEntity> = Pick<
  DataGridProProps,
  'sortModel' | 'sortingMode' | 'initialState'
> &
  BaseProps<T> &
  (SaveProp<T> | EntityProp<T>);

function isSaveProps<T extends TEntity = any>(
  props: AdminDataGridProps<T>
): props is BaseProps<T> & SaveProp<T> {
  return typeof (props as SaveProp<T>).save === 'function';
}

//const MIN_COL_WIDTH = 50;

export default function AdminDataGrid<T extends { id: number | string }>(
  props: PropsWithChildren<AdminDataGridProps<T> & TreeBaseProps<T>>
) {
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const apiRef = useGridApiRef();
  const { t } = useTranslation();


  const [coordinatesOfMenu, setCoordinatesOfMenu] = useState<{
    X?: number;
    Y?: number;
  }>({});
  const [ctxMenuOpen, setCtxMenuOpen] = useState<boolean>(false);
  const [currentElt, setCurrentElt] = useState<T | undefined>(undefined);
  const [moveEvt, setMoveEvt] = useState<TMoveEntityEvt>('none');

  const setFetchArg = props.setFetchArg;


  const ImportComponent = props.ImportComponent;
  const { setPage, setPageSize, sortModel, setSortModel, refresh, ...list } =
    useDataGridPagination<T>({
      count: props.rowCount,
      setArgs: setFetchArg,
      prefetch: props.prefetch,
      refetch: props.refetch,
      defaultFilter: props.defaultFilter
    });

  // Ctx menu
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();


  // Delete confirm
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<T | undefined>(undefined);
  const [logEntry, setLogEntry] = useState<T | undefined>(undefined);
  const {
    editMode: routeEditMode,
    navQuery,
    entityId: routeEntityId,
    searchTerm: term,
    ...routes
  } = useDataGridRoutes();

  const [editValue, setEditValue] = useState<T | undefined>(undefined);
  const [validateErrorValue, setValidateErrorValue] = useState<ErrorObject | undefined>(undefined);

  const { canCreate, canEdit, canCopy, canDelete } = useRegisterAccessRightsEvaluation({
    registerId: props.registerId,
    canCreate: props.canCreate,
    canEdit: props.canEdit,
    canCopy: props.canCopy ?? props.canCopyEntity?.(currentElt as T),
    canDelete: props.canDelete,
    extendSimpleAccess: props.extendSimpleAccess
  });

  const {
    resetColumnSettings,
    updateSettingsColumn,
    saveSettings,
    configuredColumns,
    pageSizeSetting,
    isFetchingSetting,
    settingsTable: tableSettings,
  } = useDataGridSettings({
    gridId: `${props.showAsTreeView ? 'tree_grid' : 'grid'}_${props.registerId}`,
    columns: props.columns,
    defaultSortParam: props?.defaultFetchSetting?.param
  });

  useEffect(() => {
    setFetchArg(prev => {
      return { ...prev, skip: isFetchingSetting };
    });
  }, [isFetchingSetting, setFetchArg]);

  useEffect(() => {
    if (term && props.setTreeView) props.setTreeView(false);
  }, [props, term]);

  useEffect(() => {
    const handleWindowClick = (evt: MouseEvent) => {
      setCtxMenuOpen(false);
    };
    if (ctxMenuOpen) {
      window.addEventListener('click', handleWindowClick, {
        capture: true,
      });
      return () =>
        window.removeEventListener('click', handleWindowClick, {
          capture: true,
        });
    }
  }, [ctxMenuOpen]);

  const handleSortModelChange = useCallback(
    (params: GridSortModel) => {
      if (props.loading) return;
      const newSortModel = params?.[0];
      if (
        !!newSortModel?.field &&
        !!sortModel &&
        !!sortModel[0]?.field &&
        newSortModel?.field === sortModel[0].field &&
        newSortModel?.sort === sortModel[0].sort
      )
        return;
      setSortModel(params);
      const columnSettings = {
        ...tableSettings,
      } as ColumnSettings;

      columnSettings.orderColumn = newSortModel?.field;
      columnSettings.ascending = newSortModel?.sort === 'asc';

      saveSettings(columnSettings);
    },
    [setSortModel, sortModel, tableSettings, saveSettings, props.loading]
  );

  const setEditPanelWidth = useCallback(
    (params: number) => {
      const columnSettings = {
        ...tableSettings,
      } as ColumnSettings;
      columnSettings.editPanelWidth = params;
      saveSettings(columnSettings);
    },
    [saveSettings, tableSettings]
  );

  const pageSizeHandler = useCallback(
    (size: number) => {
      const columnSettings = {
        ...tableSettings,
        pageSize: size,
      } as ColumnSettings;
      saveSettings(columnSettings);
      setPageSize(size);
    },
    [saveSettings, tableSettings, setPageSize]
  );

  useEffect(() => {
    setPageSize(pageSizeSetting ?? 50);
  }, [pageSizeSetting, setPageSize]);

  useEffect(() => {
    if (routeEditMode === EditorModus.Soek && props.setTreeView)
      props.setTreeView(false);
  }, [routeEditMode, props]);

  const save = (props as SaveProp<T>).save;
  const { searchEntity } = props as EntityProp<T>;

  const searchItemCallback = useCallback(
    (q: string | T) => {
      if (list.pageSize > 0) {
        setFetchArg?.((prev) => {
          return {
            ...prev,
            param: { skip: 0, top: list.pageSize },
            query: q,
          };
        });
      }
    },
    [setFetchArg, list.pageSize]
  );

  useEffect(() => {
    if (typeof term === 'string') {
      if (searchEntity) {
        searchEntity(term);
      } else if (save) {
        save(term as unknown as T, EditorModus.Soek);
      } else {
        searchItemCallback(term);
      }
    }
  }, [searchEntity, save, term, dispatch, searchItemCallback]);

  useEffect(() => {
    if (props.getApiGridRef) {
      props.getApiGridRef(apiRef);
    }
  }, [apiRef, props]);

  function handleResize(params: GridColumnResizeParams) {
    let datColDef: GridColDef = params.colDef;
    let width: number = params.width;
    updateSettingsColumn([
      ...columns
        .filter((i) => i.field !== 'action')
        .map((i: GridColDef) => {
          if (
            i.field === datColDef.field ||
            (datColDef.field === '__tree_data_group__' &&
              i.field === columns[0].field)
          )
            return {
              ...i,
              width: width,
              flex: undefined,
            } as GridColDef;
          return i;
        }),
    ]);
  }

  function handleActionColumnClick(
    evt: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    row: T
  ) {
    setCtxMenuOpen(true);
    setAnchorEl(evt.currentTarget);
    setCoordinatesOfMenu({ X: undefined, Y: undefined });
    evt.stopPropagation();
    setCurrentElt(row);
  }

  const columns: GridColumns = [
    ...configuredColumns,
    {
      field: 'action',
      type: 'actions',
      hide: props.hideContextMenu,
      resizable: false,
      width: 45,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<MaterialUIIcons.MoreVertIcon />}
          label={'actions'}
          test-id={props.testId + '-itemMenu-btn'}
          showInMenu={false}
          onClick={(evt) =>
            handleActionColumnClick(evt, params.row as unknown as T)
          }
        />,
      ],
    },
  ];

  const closeEditPanel = useCallback(
    function closeEditPanel() {
      setEditValue(undefined);
      setValidateErrorValue(undefined);
      routes.navList();
    },
    [routes]
  );

  const { saveChangesEditPanel, responseValue, responseObjectError } = useSaveDataGridEditPanel<T>(
    {
      isSaveProps: isSaveProps<T>(props),
      getId: props.getId,
      save: (props as SaveProp<T>).save,
      editMode: routeEditMode,
      closeEditPanel: closeEditPanel,
      searchItemCallback: searchItemCallback,
      navEdit: routes.navEdit,
      updateEntity: (props as EntityProp<T>).updateEntity,
      createEntity: (props as EntityProp<T>).createEntity
    });

  useEffect(() => {
    if (responseObjectError)
      setValidateErrorValue(responseObjectError);
  }, [responseObjectError]);

  useEffect(() => {
    if (responseValue)
      setEditValue(responseValue);
  }, [responseValue]);

  let error: { message: string, title?: string } | undefined;
  if (typeof props.error === 'string') {
    if (props?.error?.length > 0) {
      error = { message: props.error };
      if (props.error === 'Ingen tilgang')
        error = { message: 'Du har ikke tilgang til registrene i Admin+', title: '' };
    } else {
      error = undefined;
    }
  } else {
    error = props.error;
  }
  const editPanelOpen = routes.dialog === 'edit';

  function rowClick(p: GridRowParams<GridRowModel>) {
    const rowEntity = p.row as unknown as T;
    if (!rowEntity) return;
    const id = getId(rowEntity);
    //let id = p.row?.id;
    setEditValue(undefined);
    setValidateErrorValue(undefined);
    //if (props.getId) id = props.getId(id);
    if (props.treeClickOnRow && props.setTreeClickOnRow)
      props.setTreeClickOnRow(false);

    if (props.treeClickOnRow) return;

    setMoveEvt('clickEntity');

    if (routeEntityId === id?.toString() && editPanelOpen) {
      closeEditPanel();
      return;
    }

    if (canEdit && (!props.canEditEntity || props.canEditEntity(rowEntity))) routes.navEdit(id);
    else routes.navView(id);
  }

  function handleKeys(e: any) {
    if (e.key === 'Escape' && editPanelOpen) {
      e.preventDefault();
      closeEditPanel();
      return;
    }
  }

  const getId = useCallback((e: T) => {
    let id = e && e.id;
    if (props.getId) id = props.getId(e);
    //return encodeURIComponent(id);

    return id;
  }, [props]);

  function handleContextMenu(e: any) {
    e.preventDefault();
    setCtxMenuOpen(false);
    const id = e.currentTarget.getAttribute('data-id');
    if (e?.currentTarget?.dataset?.rowindex) {
      setCoordinatesOfMenu({ X: e.clientX, Y: e.clientY });
      setCurrentElt(apiRef.current.getRow(id) as unknown as T);
      setCtxMenuOpen(true);
      setAnchorEl(e.target);
    }
  }

  const editPanel = useMemo(() => {
    return (
      <AdminDataGridEditPanel
        getHeaderTextEditPanel={props.getHeaderText}
        itemTitle={props.itemTitle}
        loading={props.mutation ?? false}
        registerId={props.registerId}
        mode={routeEditMode}
        value={editValue}
        testId={props.testId}
        setValue={setEditValue}
        id={routeEntityId}
        moveEvt={moveEvt}
        setMoveEvt={setMoveEvt}
        requiredFields={props.requiredFields}
        getErrors={props.getErrors}
        searchTerm={term}
        initialValidateError={validateErrorValue}
        editPanelWidth={tableSettings.editPanelWidth ?? (window.innerWidth / 2)}
        open={editPanelOpen}
        getDisplayName={props.getDisplayName}
        onClose={closeEditPanel}
        saveChanges={saveChangesEditPanel}
        setEditPanelWidth={setEditPanelWidth}
        renderDetailView={props.renderDetailView}
        canCreate={canCreate}
        canCopy={canCopy}
        canEdit={canEdit}
      />
    );
  }, [
    closeEditPanel,
    editValue,
    saveChangesEditPanel,
    setEditPanelWidth,
    tableSettings.editPanelWidth,
    term,
    routeEditMode,
    editPanelOpen,
    routeEntityId,
    props.getDisplayName,
    props.getErrors,
    props.getHeaderText,
    validateErrorValue,
    props.itemTitle,
    props.registerId,
    props.renderDetailView,
    props.requiredFields,
    props.testId,
    props.mutation,
    moveEvt,
    canCopy,
    canCreate,
    canEdit
  ]);

  const [confirmMessage, setConfirmMessage] = useState<string | ReactElement>(t('Er du sikker på at du vil slette?') ?? '');
  const selectionModel = useMemo(() => {
    if (routeEntityId)
      return [routeEntityId] as GridRowId[];
    return [];
  }, [routeEntityId]);
  return (
    <>
      <div
        className={classes.container}
        onKeyDown={(e) => {
          handleKeys(e);
        }}
      >
        <div className={classes.gridContainer}>
          {props.showAsTreeView ? (
            <Controls.DataGrid
              pageSize={list.pageSize}
              rowsPerPageOptions={[25,50, 100, 150, 200]}
              onPageSizeChange={pageSizeHandler}
              onPageChange={setPage}
              pagination
              paginationMode='server'
              //pageSize={props.rows?.length}
              rowCount={props.rowCount}
              treeData={props.treeData}
              pinnedColumns={{ right: ['action'] }}
              isGroupExpandedByDefault={props.isGroupExpandedByDefault}
              groupingColDef={{
                ...props.groupingColDef,
                width: columns[0].width,
              }}
              getTreeDataPath={props.getTreeDataPath}
              className={classes.root}
              selectionModel={selectionModel}
              rows={props.rows}
              columns={[...columns.slice(1).map(i => {
                return { ...i, sortable: false };
              })]}
              headerHeight={40}
              defaultGroupingExpansionDepth={-1}
              rowHeight={35}
              onColumnWidthChange={(params: GridColumnResizeParams) =>
                handleResize(params)
              }

              apiRef={apiRef}
              loading={props.loading || isFetchingSetting}
              components={{
                Toolbar: CustomToolbar as any,
                ErrorOverlay: CustomErrorOverlay as any,
                NoRowsOverlay: GridNoRowsCustomOverlay
              }}
              componentsProps={{
                toolbar: {

                  updateColumns: updateSettingsColumn,
                  columns: configuredColumns,
                  headerName: props.registerTitle,
                  haveTreeView: props.haveTreeView,
                  setTreeView: props.setTreeView,
                  page: list.page,
                  testId: props.testId,
                  searchTerm: term,
                  canCreate,
                  showFilter: TypeFilter.client === props.filterType,
                  children: props.toolbarItems,
                  pageSize: list.pageSize,
                  loadEksport: props.loadEksport,
                  currentFilter: list.currentFilter,
                  onChangeSelectedFilter: list.setCurrentFilter,
                  onChangeSelectedFilterGroups: props.onChangeSelectedFilterGroups,
                  defaultFilters: props.filters,
                  showAsTreeView: props.showAsTreeView,
                  showPagination: props.showPagination,
                  rowCount: props.rowCount,
                  exportComponentProps: props.exportComponentProps,
                  ImportComponent: () =>
                    ImportComponent ? (
                      <ImportComponent
                        onLoaded={props.refresh ? props.refresh : refresh}
                      />
                    ) : (
                      <></>
                    ),
                  refresh: props.refresh ? props.refresh : refresh,
                  resetColumnSettings,
                },
                errorOverlay: {
                  updateColumns: updateSettingsColumn,
                  columns: configuredColumns,
                  headerName: props.registerTitle,
                  rowCount: props.rowCount,
                  refresh: props.refresh,
                  resetColumnSettings,
                },
                row: {
                  onContextMenu: handleContextMenu,
                },
                noRowsOverlay: {
                  searchMode: props.searchMode
                }
              }}
              hideFooterPagination={true}
              disableColumnSelector={true}
              hideFooter={true}
              disableColumnMenu
              onRowClick={props.rowClick ?? rowClick}
            />
          ) : (
            <Controls.DataGrid
              className={classes.root}
              pinnedColumns={{ right: ['action'] }}
              rows={props.rows}
              page={list.page}
              rowCount={props.rowCount}
              columns={columns}
              apiRef={apiRef}
              headerHeight={40}
              selectionModel={selectionModel}
              rowHeight={35}
              sortingOrder={['asc', 'desc', null]}
              paginationMode="server"
              sortModel={props.sortModel ?? sortModel}
              sortingMode={props.sortingMode ?? 'server'}
              pageSize={list.pageSize}
              rowsPerPageOptions={[25, 50, 100, 150, 200]}
              onColumnWidthChange={(params: GridColumnResizeParams) =>
                handleResize(params)
              }
              onSortModelChange={handleSortModelChange}
              onPageSizeChange={pageSizeHandler}
              onPageChange={setPage}
              pagination
              loading={props.loading}
              error={error}
              components={{
                Toolbar: CustomToolbar as any,
                ErrorOverlay: CustomErrorOverlay as any,
                NoRowsOverlay: GridNoRowsCustomOverlay
              }}
              componentsProps={{
                toolbar: {
                  updateColumns: updateSettingsColumn,
                  searchTerm: term,
                  testId: props.testId,
                  columns: configuredColumns,
                  headerName: props.registerTitle,
                  loadEksport: props.loadEksport,
                  onCreate: props.onCreate,
                  page: list.page,
                  children: props.toolbarItems,
                  pageSize: list.pageSize,
                  haveTreeView: props.haveTreeView,
                  currentFilter: list.currentFilter,
                  onChangeSelectedFilter: list.setCurrentFilter,
                  onChangeSelectedFilterGroups: props.onChangeSelectedFilterGroups,
                  defaultFilters: props.filters,
                  setTreeView: props.setTreeView,
                  exportComponentProps: props.exportComponentProps,
                  showAsTreeView: props.showAsTreeView,
                  showPagination: props.showPagination,
                  rowCount: props.rowCount,
                  canCreate,
                  ImportComponent: () =>
                    ImportComponent ? (
                      <ImportComponent onLoaded={refresh} />
                    ) : (
                      <></>
                    ),
                  refresh,
                  resetColumnSettings,
                },
                row: {
                  onContextMenu: handleContextMenu,
                },
                errorOverlay: {
                  updateColumns: updateSettingsColumn,
                  columns: configuredColumns,
                  headerName: props.registerTitle,
                  rowCount: props.rowCount,
                  refresh,
                  resetColumnSettings,
                },
                noRowsOverlay: {
                  searchMode: props.searchMode
                }
              }}
              hideFooterPagination={true}
              hideFooter={true}
              disableColumnSelector={true}
              disableColumnMenu
              onRowClick={props.rowClick ?? rowClick}
            />
          )}
          {props.hideContextMenu ? <></> : <AdminDataGridContextMenu
            anchorEl={anchorEl}
            isTreeView={props.showAsTreeView ?? false}
            onClose={() => setCtxMenuOpen(false)}
            open={ctxMenuOpen}
            entity={currentElt}
            testId={props.testId}
            canCreate={canCreate}
            canCopy={currentElt != null && canCopy && (!props.canCopyEntity || props.canCopyEntity(currentElt))}
            canDelete={currentElt != null && canDelete && (!props.canDeleteEntity || props.canDeleteEntity(currentElt))}
            canEdit={currentElt != null && canEdit && (!props.canEditEntity || props.canEditEntity(currentElt))}
            X={coordinatesOfMenu.X}
            Y={coordinatesOfMenu.Y}
            openCopy={(e) => {
              e && routes.navCopy(getId(e));
            }}
            openView={(e) => e && routes.navView(getId(e))}
            expandChildrenNodes={props.expandChildrenNodes}
            expandChildrenLessNodes={props.expandChildrenLessNodes}
            expandAllNodes={props.expandAllNodes}
            hideAllCallBack={props.hideAllCallBack}
            configureCtxMenuItems={props.configureCtxMenuItems}
            openDelete={(e) => {
              props.deleteReport(getId(e)).then(
                (resp) => {
                  if (resp.status === QueryStatus.fulfilled) {
                    setDeleteEntry(e);
                    if (props.getConfirmRemoveMessage) {
                      props.getConfirmRemoveMessage(e!).then(i => {
                        setConfirmMessage(i);
                        setConfirmDeleteOpen(true);
                      });
                      return;
                    }
                    setConfirmMessage(t('Er du sikker på at du vil slette?') ?? '');
                    setConfirmDeleteOpen(true);
                  }
                }
              );
            }}
            createChild={(e) => e && routes.navCreateChild((getId(e)))}
            openEdit={(e) => e && routes.navEdit(getId(e))}
            openLog={(e) => {
              e && routes.navLog(encodeURIComponent(getId(e)));
              setLogEntry(e);
            }}
          />}
        </div>
      </div>
      {editPanelOpen && editPanel}
      <LogDialog
        open={routes.dialog === 'log'}
        detailsMode={routeEditMode === EditorModus.Vis && routes.logId != null}
        logId={routes.logId}
        close={routes.navList}
        subtitle={
          routeEntityId
            ? props.getDisplayName?.(routeEntityId, EditorModus.Logg) ?? ''
            : ''
        }
        id={routeEntityId}
        logUrl={props.getLogUrl((logEntry) as T)}
        onLogOpen={routes.navLogDetails}
        goBackToLogList={() => (routeEntityId ? routes.navLog(routeEntityId) : {})}
      />

      <ConfirmDialog
        title={props.confirmDeleteTitle ?? `Slette ${props.getDisplayName(deleteEntry?.id as T['id'], routeEditMode) ?? ''}`}
        open={confirmDeleteOpen}
        hideOnCloseIcon
        onClose={() => {
          setConfirmDeleteOpen(false);
        }}
        onConfirm={async () => {
          try {
            if (!deleteEntry) return;
            const deleteId = props.getId ? props.getId(deleteEntry) : deleteEntry.id;
            if (editPanelOpen && routeEntityId === deleteId) closeEditPanel();
            await props.remove(deleteEntry!);
          } finally {
            setConfirmDeleteOpen(false);
          }
        }}
      >
        {confirmMessage}
      </ConfirmDialog>
    </>
  );
}

